/*
 * Purpose: Rotating home headline — split-flap departure board (Vestaboard-style)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 99
 */
import { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { SplitFlapSlot, FlapIdleChar, splitFlapPhraseDurationMs } from '@/components/home/SplitFlapChar';
import {
  HOME_HEADLINE_BOARDS,
  HEADLINE_BOARD_ROWS,
  boardSlotsForHeadline,
  type HeadlineBoardDef,
} from '@/components/home/headlineBoard';

export const HOME_HEADLINE_PHRASES = HOME_HEADLINE_BOARDS.map((b) => b.text);

const MIN_HOLD_MS = 2400;

function FlipBoardRow({
  board,
  tick,
  staticMode,
}: {
  board: HeadlineBoardDef;
  tick: number;
  staticMode?: boolean;
}) {
  const slots = useMemo(() => boardSlotsForHeadline(board), [board]);

  const rows = useMemo(() => {
    const grouped: (typeof slots)[] = Array.from({ length: HEADLINE_BOARD_ROWS }, () => []);
    for (const slot of slots) {
      grouped[slot.row].push(slot);
    }
    return grouped;
  }, [slots]);

  return (
    <span className="ab-flip-board-stack" aria-hidden>
      {rows.map((rowSlots, rowIndex) => (
        <span key={rowIndex} className="ab-flip-row">
          {rowSlots.map((slot) => {
            const phraseLength = slot.rowVisibleLength;
            const isTrailingPad = slot.col >= phraseLength;

            if (staticMode) {
              if (isTrailingPad) return null;
              if (slot.char === ' ') {
                return <span key={slot.slotIndex} className="ab-flip-gap" aria-hidden />;
              }
              return (
                <span key={slot.slotIndex} className="ab-flip-cell ab-flip-cell--static">
                  <FlapIdleChar char={slot.char} />
                </span>
              );
            }

            return (
              <SplitFlapSlot
                key={slot.slotIndex}
                char={slot.char}
                slotIndex={slot.slotIndex}
                col={slot.col}
                tick={tick}
                rowVisibleLength={phraseLength}
              />
            );
          })}
        </span>
      ))}
    </span>
  );
}

export function RotatingOutcomeHeadline() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const board = HOME_HEADLINE_BOARDS[index];

  const intervalMs = useMemo(() => {
    const flipMs = splitFlapPhraseDurationMs(board.text.length);
    return Math.max(MIN_HOLD_MS + flipMs, 4800);
  }, [board.text.length]);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % HOME_HEADLINE_BOARDS.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [reduceMotion, intervalMs]);

  if (reduceMotion) {
    const staticBoard = HOME_HEADLINE_BOARDS[0];
    return (
      <h2 className="ab-flip-board ab-flip-board--static text-center px-2" aria-live="polite">
        <span className="sr-only">{staticBoard.text}</span>
        <FlipBoardRow board={staticBoard} tick={0} staticMode />
      </h2>
    );
  }

  return (
    <h2
      className="ab-flip-board text-center px-2 min-h-[5rem] sm:min-h-[5.5rem]"
      aria-live="polite"
    >
      <span className="sr-only">{board.text}</span>
      <FlipBoardRow board={board} tick={index} />
    </h2>
  );
}
